import moment from 'moment'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {FieldNames, findFieldName} from '../../../../fields'
import {useDirtyFields} from '../../../../form/isDirty/useDirtyFields'
import {
  ChangeableField,
  ChangeableFieldConfiguration,
  ChangeableFieldConfigurations,
  ChangeableFields
} from '../types'

/**
 * Custom hook that manages changeable field states based on time-based business rules
 * and form dirty state. This is the core hook for determining whether fields can be
 * changed, require confirmation, or are locked based on time constraints.
 *
 * @param changeableFieldsConfiguration - Array of field configurations with timing constraints
 * @param refreshIntervalMs - Optional interval in milliseconds to refresh field states (0 = no refresh)
 * @returns ChangeableFields array with current state for each field
 *
 * Business Rules:
 * - 'lifeCycle' fields: always 'autoConfirmable' when enabled
 * - 'leadTime' fields are 'autoConfirmable' before autoConfirmableUntil time
 * - 'leadTime' fields require 'requireDispatchConfirmation' between autoConfirmableUntil and changeableUntil
 * - 'leadTime' fields are 'notChangeable' after changeableUntil time
 * - All fields are 'notChangeable' when disabled
 * - Dirty state is tracked independently for UI feedback
 */
export const useBaseChangeableFieldsRules = (
  changeableFieldsConfiguration: ChangeableFieldConfigurations,
  refreshIntervalMs: number = 0
) => {
  // Extract field names for dirty field tracking
  // Memoized to prevent unnecessary re-renders when configuration objects change
  const fieldNames = useMemo(
    () => changeableFieldsConfiguration.map((x) => x.fieldName),
    [changeableFieldsConfiguration]
  )

  // Track which fields have been modified by the user
  const dirtyFields = useDirtyFields(fieldNames)

  // State to hold current changeable field states
  const [changeableFieldStates, setChangeableFieldStates] = useState<ChangeableFields>(() =>
    getChangeableFields(changeableFieldsConfiguration, dirtyFields)
  )

  // Memoized function to recalculate field states based on current time and dirty state
  // This is called both on dependency changes and on timer intervals
  const updateFieldStates = useCallback(() => {
    const updatedStates = getChangeableFields(changeableFieldsConfiguration, dirtyFields)
    setChangeableFieldStates(updatedStates)
  }, [changeableFieldsConfiguration, dirtyFields])

  // Effect to handle both immediate updates and periodic refresh
  // Updates field states when dependencies change and optionally sets up interval for time-based updates
  useEffect(() => {
    // Immediately update states when dependencies change
    updateFieldStates()

    // Only set up interval if refresh is requested (> 0)
    if (refreshIntervalMs <= 0) {
      return
    }

    // Set up periodic refresh to handle time-based state transitions
    // This ensures fields transition from autoConfirmable -> requireDispatchConfirmation -> notChangeable
    // at the exact moments defined by the time constraints
    const id = setInterval(() => {
      updateFieldStates()
    }, refreshIntervalMs)

    // Cleanup interval on component unmount or dependency change
    return () => clearInterval(id)
  }, [updateFieldStates, refreshIntervalMs])

  return changeableFieldStates
}

/**
 * Pure function that calculates changeable field states for all configured fields
 * at a specific point in time. This function is called both initially and on refresh intervals.
 *
 * @param configurations - Array of field configurations with timing constraints
 * @param dirtyFields - Array of field names that have been modified by the user
 * @param currentTime - Current UTC time (defaults to current moment, injectable for testing)
 * @returns Array of changeable fields with their current states and dirty status
 */
const getChangeableFields = (
  configurations: ChangeableFieldConfigurations,
  dirtyFields: FieldNames[],
  currentTime: moment.Moment = moment.utc()
): ChangeableFields =>
  configurations.map(({fieldName, ...config}) => ({
    fieldName,
    ...getChangeableField(fieldName, config, dirtyFields, currentTime)
  }))

/**
 * Calculates the changeable state for a single field based on business rules and timing constraints.
 * This function implements the core business logic for field changeability.
 *
 * @param fieldName - The specific field to check (includes field and optional fieldPart)
 * @param config - Configuration object with type, isEnabled flag and timing constraints
 * @param dirtyFields - Array of fields that have been modified by the user
 * @param currentTime - Current UTC time for time-based comparisons
 * @returns ChangeableField object with state and dirty status
 *
 * State Transition Logic:
 * 1. If config.isEnabled = false -> 'notChangeable'
 * 2. If config.type = 'lifeCycle' -> 'autoConfirmable'
 * 3. If config.type = 'leadTime':
 *    - If currentTime > changeableUntil -> 'notChangeable'
 *    - If currentTime > autoConfirmableUntil -> 'requireDispatchConfirmation'
 *    - Otherwise -> 'autoConfirmable'
 */
const getChangeableField = (
  fieldName: FieldNames,
  config: ChangeableFieldConfiguration,
  dirtyFields: FieldNames[],
  currentTime: moment.Moment = moment.utc()
): ChangeableField => {
  // Check if this field is dirty, only exact matches
  const isDirty = !!findFieldName(dirtyFields, fieldName, false)

  // First check: if field is disabled in configuration, it's not changeable regardless of time
  if (!config.isEnabled) {
    return {state: 'notChangeable', isDirty}
  }

  // Handle lifeCycle type - always autoConfirmable when enabled
  if (config.type === 'lifeCycle') {
    return {state: 'autoConfirmable', isDirty}
  }

  // Handle leadTime type - time-based rules apply
  const changeableUntil = moment.utc(config.changeableUntil)
  const autoConfirmableUntil = moment.utc(config.autoConfirmableUntil)

  // Second check: if we're past the final changeable deadline, field is locked
  if (currentTime.isAfter(changeableUntil)) {
    return {state: 'notChangeable', isDirty}
  }

  // Third check: if we're past auto-confirmable deadline but before changeable deadline,
  // field requires dispatcher confirmation to change
  if (currentTime.isAfter(autoConfirmableUntil)) {
    return {
      state: 'requireDispatchConfirmation',
      isDirty
    }
  }

  // Default state: field can be changed and will be auto-confirmed
  return {
    state: 'autoConfirmable',
    isDirty
  }
}
